// Client-side mirror of backend/internal/forms/engine.go. The server is the
// authority (it re-checks on submit); these helpers drive the UI only.
import { evaluate } from '@/lib/expr'
import type { Answers, FieldOption, FormDefinition, FormField, FormPage } from '../types'

export function pageActive(page: FormPage, answers: Answers): boolean {
    return !page.activeWhen || evaluate(page.activeWhen, answers)
}

export function activePages(def: FormDefinition, answers: Answers): FormPage[] {
    return def.pages.filter((p) => pageActive(p, answers))
}

export function fieldVisible(f: FormField, answers: Answers): boolean {
    return !f.hidden && (!f.visibleWhen || evaluate(f.visibleWhen, answers))
}

export function isAnswered(v: unknown): boolean {
    if (typeof v === 'string') return v.trim() !== ''
    if (Array.isArray(v)) return v.length > 0
    return v !== null && v !== undefined
}

/** Keys of visible, required, unanswered fields on the active pages. */
export function missingRequired(def: FormDefinition, answers: Answers): string[] {
    const missing: string[] = []
    for (const p of activePages(def, answers)) {
        for (const f of p.fields) {
            if (f.type === 'html' || !f.required || !fieldVisible(f, answers)) continue
            if (!isAnswered(answers[f.key])) missing.push(f.key)
        }
    }
    return missing
}

export function missingOnPage(page: FormPage, answers: Answers): string[] {
    return page.fields
        .filter((f) => f.type !== 'html' && f.required && fieldVisible(f, answers) && !isAnswered(answers[f.key]))
        .map((f) => f.key)
}

export function optionLabel(options: FieldOption[] | undefined, value: string): string {
    return options?.find((o) => o.value === value)?.label ?? value
}

/** Human-readable text for an answer (select values mapped to their labels). */
export function displayAnswer(f: FormField, v: unknown): string {
    if (!isAnswered(v)) return ''
    if (f.type === 'yes_no') return v === 'yes' ? 'Yes' : v === 'no' ? 'No' : String(v)
    if (Array.isArray(v)) return v.map((x) => optionLabel(f.options, String(x))).join(', ')
    if (f.type === 'single_select') return optionLabel(f.options, String(v))
    return String(v)
}

export function fieldIndex(def: FormDefinition): Record<string, FormField> {
    const idx: Record<string, FormField> = {}
    for (const p of def.pages) for (const f of p.fields) idx[f.key] = f
    return idx
}
